'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { trackPublicPage, isUsageEvent } from '../lib/umami'

// Only the homepage and a successfully loaded, indexable Nextra page render
// this owner. Props are fixed public metadata from the validated server route.
export default function UmamiPageview({ path, title, outcome }: { path: string; title: string; outcome?: 'search_results' | 'search_empty' }) {
  const pathname = usePathname()
  const visit = useRef({})
  const outcomeSent = useRef(false)

  useEffect(() => {
    if (pathname !== path) return
    let current = true
    // Search is deliberately excluded from pageviews; only the fixed outcome is measured.
    if (path !== '/search') void trackPublicPage(path, title, () => current, visit.current)
    if (outcome && !outcomeSent.current) {
      // A microtask allows Strict Mode's abandoned effect to cancel before marking sent.
      void Promise.resolve().then(() => {
        if (!current || outcomeSent.current) return
        outcomeSent.current = true
        void trackPublicPage.event(path, title, outcome, () => current)
      })
    }
    function usage(event: Event) {
      if (event instanceof CustomEvent && isUsageEvent(event.detail)) {
        void trackPublicPage.event(path, title, event.detail, () => current)
      }
    }
    function click(event: MouseEvent) {
      const target = event.target instanceof Element ? event.target.closest('a[data-docs-event], a[download]') : null
      if (!target) return
      const name = target.hasAttribute('download') ? 'download_start' : target.getAttribute('data-docs-event')
      if (isUsageEvent(name)) {
        // Dispatch before SPA navigation unmounts this article owner.
        void trackPublicPage.event(path, title, name, () => current)
      }
    }
    document.addEventListener('fivemdocs:usage', usage)
    document.addEventListener('click', click)
    return () => {
      current = false
      document.removeEventListener('fivemdocs:usage', usage)
      document.removeEventListener('click', click)
    }
  }, [path, pathname, title, visit, outcome])

  return null
}
